import '../../../../assets/css/screens/serviceprovider/dashboard/subscription/WorkerSubHistory.css';

import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, Star, Crown, Check, Clock,
  AlertTriangle, XCircle, ChevronRight, RefreshCw,
  CalendarDays, Receipt, ShieldCheck,
} from 'lucide-react';
import axios from 'axios';
import WorkerNavbar from '../../../../components/serviceprovider/workerdashboard/Workernavbar/WorkerNavbar';
import WorkerSubscriptionHistorySkeleton from "../../../../components/skeltonloading/WorkerSubscriptionHistorySkeleton";
import AppContext from '../../../../context/AppContext';

/* ── tier helpers (agar backend tier na bheje to Basic default) ── */
const TIER_ICON  = { Basic: <Zap size={16} />, Pro: <Star size={16} />, Elite: <Crown size={16} /> };
const TIER_CLASS = { Basic: 'wshi-card--basic', Pro: 'wshi-card--pro', Elite: 'wshi-card--elite' };
const TIER_COLOR = { Basic: '#0491e1', Pro: '#7c3aed', Elite: '#d97706' };

/* ── live countdown hook ── */
function useCountdown(endDateStr) {
  const calc = () => {
    if (!endDateStr) return null;
    const diff = new Date(endDateStr) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000)  / 60000);
    const s = Math.floor((diff % 60000)    / 1000);
    const urgent = d === 0 && h < 24;
    return { days: d, hours: h, minutes: m, seconds: s, expired: false, urgent };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    if (!endDateStr) return;
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [endDateStr]);
  return time;
}

/* ── CountdownBlock ── */
function CountdownBlock({ endDate }) {
  const t = useCountdown(endDate);
  if (!t) return null;
  if (t.expired) return (
    <div className="wshi-timer wshi-timer--expired">
      <XCircle size={14} /> Plan expired
    </div>
  );
  return (
    <div className={`wshi-timer${t.urgent ? ' wshi-timer--urgent' : ''}`}>
      {t.urgent && <AlertTriangle size={13} />}
      <div className="wshi-timer-units">
        {[['d', t.days], ['h', t.hours], ['m', t.minutes], ['s', t.seconds]].map(([lbl, val]) => (
          <div className="wshi-timer-unit" key={lbl}>
            <span className="wshi-timer-num">{String(val).padStart(2, '0')}</span>
            <span className="wshi-timer-lbl">{lbl}</span>
          </div>
        ))}
      </div>
      <span className="wshi-timer-text">remaining</span>
    </div>
  );
}

/* ── StatusPill ── */
function StatusPill({ status }) {
  const map = {
    active:    { label: 'Active',    cls: 'wshi-pill--active'    },
    expired:   { label: 'Expired',   cls: 'wshi-pill--expired'   },
    cancelled: { label: 'Cancelled', cls: 'wshi-pill--cancelled' },
    pending:   { label: 'Pending',   cls: 'wshi-pill--pending'   },
  };
  const cfg = map[status?.toLowerCase()] || map.pending;
  return <span className={`wshi-pill ${cfg.cls}`}>{cfg.label}</span>;
}

/* ── format date ── */
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
export default function WorkerSubscriptionHistory() {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);

  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ── current subscription (worker ki API — unchanged) ── */
  const getCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${baseUrl}/current/subscription`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (response.data.success) {
        setCurrent(response.data.subscription);
      }
    } catch (error) {
      console.log(error);
      setError("Unable to load subscription history.");
    }
  };

  /* ── billing history (worker ki API — unchanged) ── */
  const getSubscriptionHistory = async () => {
    try {
      const response = await axios.get(`${baseUrl}/subscription/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (response.data.success) setHistory(response.data.history || []);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([getCurrentSubscription(), getSubscriptionHistory()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  if (loading) {
    return <WorkerSubscriptionHistorySkeleton />;
  }

  /* ── worker ke data ko customer-jaisi shape mein normalize karna ── */
 const currentTier = current?.whsubscription?.tier || 'Basic';
const currentPlan = current?.whsubscription?.planName;
const currentPrice = current?.whsubscription?.price;
const currentPeriod = current?.whsubscription?.duration;

const currentFeatures = Array.isArray(current?.whsubscription?.features)
  ? current.whsubscription.features
  : [];
  return (
    <>
      <WorkerNavbar />
      <div className="wshi-page">

        {/* ── HEADER ── */}
        <div className="wshi-header">
          <button className="wshi-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <div className="wshi-header-text">
            <h3 className="wshi-page-title">My Subscriptions</h3>
            <p className="wshi-page-sub">Track your active plan and billing history</p>
          </div>
          <button className="wshi-refresh" onClick={fetchAll}>
            <RefreshCw size={18} />
          </button>
        </div>

        {/* ── ERROR ── */}
        {error && (
          <div className="wshi-error">
            <AlertTriangle size={28} />
            <p>{error}</p>
            <button className="wshi-retry-btn" onClick={fetchAll}>Try again</button>
          </div>
        )}

        {!error && (
          <>
            {/* ════ CURRENT PLAN ════ */}
            {current ? (
              <section className="wshi-section">
                <div className="wshi-section-label">
                  <ShieldCheck size={15} />
                  Current Plan
                </div>

                <div className={`wshi-current-card ${TIER_CLASS[currentTier] || 'wshi-card--basic'}`}>
                  {/* top row */}
                  <div className="wshi-current-top">
                    <div className="wshi-current-icon" style={{ color: TIER_COLOR[currentTier] }}>
                      {TIER_ICON[currentTier] || <Zap size={16} />}
                    </div>
                    <div className="wshi-current-info">
                      <p className="wshi-current-name">{currentPlan}</p>
                      <p className="wshi-current-meta">
                        {currentTier} · ${currentPrice} / {currentPeriod}
                      </p>
                    </div>
                    <StatusPill status={current.status} />
                  </div>

                  {/* countdown */}
                  <CountdownBlock endDate={current.end_date} />

                  {/* dates */}
                  <div className="wshi-dates">
                    <div className="wshi-date-item">
                      <CalendarDays size={13} />
                      <span>Started</span>
                      <strong>{fmtDate(current.start_date)}</strong>
                    </div>
                    <div className="wshi-date-sep" />
                    <div className="wshi-date-item">
                      <CalendarDays size={13} />
                      <span>Expires</span>
                      <strong>{fmtDate(current.end_date)}</strong>
                    </div>
                  </div>

                  {/* features */}
                 {currentFeatures.length > 0 && (
  <ul className="wshi-current-features">
    {currentFeatures.map((f, i) => (
      <li key={i}>
        <Check size={13} className="wshi-check" />

        <span>
          <strong>{f.feature_name}</strong>

          {f.feature_value
            ? `: ${f.feature_value}`
            : ''}
        </span>
      </li>
    ))}
  </ul>
)}

                  {/* upgrade CTA */}
                  <button
                    className="wshi-upgrade-btn"
                    onClick={() => navigate('/workersubscription')}
                  >
                    Upgrade Plan <ChevronRight size={15} />
                  </button>
                </div>
              </section>
            ) : (
              <div className="wshi-no-active">
                <div className="wshi-no-active-icon">💎</div>
                <p className="wshi-no-active-title">No active subscription</p>
                <p className="wshi-no-active-sub">Choose a plan to unlock premium features.</p>
                <button className="wshi-browse-btn" onClick={() => navigate('/workersubscription')}>
                  Browse Plans <ChevronRight size={15} />
                </button>
              </div>
            )}

            {/* ════ HISTORY ════ */}
            {history.length > 0 && (
              <section className="wshi-section">
                <div className="wshi-section-label">
                  <Receipt size={15} />
                  Billing History ({history.length})
                </div>

                <div className="wshi-history-list">
                  {history.map((item, i) => {
                    const itemTier = item?.whsubscription?.tier || 'Basic';
                    return (
                      <div key={item.id ?? i} className="wshi-history-item">
                        <div
                          className="wshi-history-accent"
                          style={{ background: TIER_COLOR[itemTier] || '#0491e1' }}
                        />
                        <div className="wshi-history-icon" style={{ color: TIER_COLOR[itemTier] }}>
                          {TIER_ICON[itemTier] || <Zap size={15} />}
                        </div>
                        <div className="wshi-history-body">
                          <div className="wshi-history-row1">
                            <p className="wshi-history-name">{item.whsubscription?.planName}</p>
                            <StatusPill status={item.status} />
                          </div>
                          <p className="wshi-history-meta">
                            ${item.whsubscription?.price} · {item.whsubscription?.duration} · {itemTier}
                          </p>
                          <p className="wshi-history-dates">
                            <Clock size={11} />
                            {fmtDate(item.start_date)} → {fmtDate(item.end_date)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {history.length === 0 && !current && (
              <div className="wshi-empty">
                <Receipt size={36} />
                <p>No subscription history yet.</p>
              </div>
            )}
          </>
        )}

        <div className="wshi-footer">
          <ShieldCheck size={13} /> Secure payments · Cancel anytime
        </div>

      </div>
    </>
  );
}